const ROOT_ROUTE_RESTRICTIONS = {
  search: 'search',
  captain: 'captain',
  contacts: 'contacts',
  companies: 'companies',
  reports: 'reports',
  campaigns: 'campaigns',
  portals: 'portals',
  settings: 'settings',
  profile: 'profile',
};

const SETTINGS_ROUTE_RESTRICTIONS = {
  general: 'settings_general',
  agents: 'settings_agents',
  teams: 'settings_teams',
  'assignment-policy': 'settings_assignment_policy',
  inboxes: 'settings_inboxes',
  labels: 'settings_labels',
  'custom-attributes': 'settings_custom_attributes',
  automation: 'settings_automation',
  'agent-bots': 'settings_agent_bots',
  macros: 'settings_macros',
  'canned-response': 'settings_canned_responses',
  integrations: 'settings_integrations',
  'audit-logs': 'settings_audit_logs',
  'custom-roles': 'settings_custom_roles',
  sla: 'settings_sla',
  'conversation-workflow': 'settings_conversation_workflow',
  security: 'settings_security',
  billing: 'settings_billing',
  captain: 'captain',
};

const toArray = values => {
  if (values === undefined || values === null) {
    return [];
  }

  return Array.isArray(values) ? values : [values];
};

const normalizeRestrictedFeatures = values => {
  return toArray(values)
    .map(feature => String(feature).trim())
    .filter(Boolean);
};

const normalizeRole = role => String(role || '').trim().toLowerCase();

const getForkFeatureFlags = () => {
  return globalThis?.chatwootConfig?.forkFeatureFlags || {};
};

const getGlobalRestrictedFeatures = surface => {
  const values = getForkFeatureFlags()?.restrictedFeatures;

  if (values && typeof values === 'object' && !Array.isArray(values)) {
    return normalizeRestrictedFeatures(values[surface]);
  }

  return normalizeRestrictedFeatures(values);
};

const getAccountRestrictedFeatures = (account, surface) => {
  const settings = account?.settings || {};

  const explicitSurfaceValues = settings[`restricted_features_${surface}`];
  if (Array.isArray(explicitSurfaceValues)) {
    return normalizeRestrictedFeatures(explicitSurfaceValues);
  }

  const values = settings.restricted_features;
  if (values && typeof values === 'object' && !Array.isArray(values)) {
    return normalizeRestrictedFeatures(values[surface]);
  }

  return normalizeRestrictedFeatures(values);
};

const getUnrestrictedRole = () => {
  return normalizeRole(getForkFeatureFlags()?.unrestrictedRole);
};

export const isUnrestrictedRestrictionUser = ({
  currentUser,
  currentRole,
} = {}) => {
  if (getForkFeatureFlags()?.isUnrestrictedUser === true) {
    return true;
  }

  const unrestrictedRole = getUnrestrictedRole();
  if (!unrestrictedRole) {
    return false;
  }

  const roleCandidates = [currentUser?.type, currentRole];
  return roleCandidates.some(role => normalizeRole(role) === unrestrictedRole);
};

export const getRestrictedFeaturesFromAccount = (account, options = {}) => {
  const { surface = 'frontend', currentUser, currentRole } = options;

  if (isUnrestrictedRestrictionUser({ currentUser, currentRole })) {
    return [];
  }

  const globalRestrictedFeatures = getGlobalRestrictedFeatures(surface);
  const accountRestrictedFeatures = getAccountRestrictedFeatures(
    account,
    surface
  );

  return [
    ...new Set([...globalRestrictedFeatures, ...accountRestrictedFeatures]),
  ];
};

export const resolveRestrictedFeatureFromPath = path => {
  const segments = String(path || '')
    .split('?')[0]
    .split('/')
    .filter(Boolean);

  const accountsIndex = segments.indexOf('accounts');
  if (accountsIndex === -1) {
    return null;
  }

  const rootSegment = segments[accountsIndex + 2];
  if (!rootSegment) {
    return null;
  }

  if (rootSegment !== 'settings') {
    return ROOT_ROUTE_RESTRICTIONS[rootSegment] || null;
  }

  const settingsSegment = segments[accountsIndex + 3];
  if (!settingsSegment) {
    return ROOT_ROUTE_RESTRICTIONS.settings;
  }

  return (
    SETTINGS_ROUTE_RESTRICTIONS[settingsSegment] ||
    ROOT_ROUTE_RESTRICTIONS.settings
  );
};

export const isFeatureRestrictedForAccount = (
  account,
  featureKey,
  options = {}
) => {
  if (!featureKey) {
    return false;
  }

  const { surface = 'frontend' } = options;
  const restrictedFeatures = getRestrictedFeaturesFromAccount(account, {
    ...options,
    surface,
  });

  if (
    surface === 'api' &&
    String(featureKey).startsWith('settings_') &&
    restrictedFeatures.includes('settings')
  ) {
    return true;
  }

  return restrictedFeatures.includes(featureKey);
};
